import React, { useMemo } from 'react';
import { Form, Dropdown, Message } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { CalendarResourceDTO } from '../../app/models/Calendar';

interface CapacitySelectorProps {
  resource: CalendarResourceDTO | null;
  selectedCapacity: number;
  onCapacityChange: (capacity: number) => void;
  availableCapacity?: number;
  required?: boolean;
}

const CapacitySelector: React.FC<CapacitySelectorProps> = observer(({
  resource,
  selectedCapacity,
  onCapacityChange,
  availableCapacity,
  required = false
}) => {
  const { t } = useTranslation();

  // FIXED: Handle number[] instead of object[]
  const capacityOptions = useMemo(() => {
    if (!resource) return [];
    
    // FIXED: availablePartitions is number[], not object[]
    const partitions = resource.availablePartitions || [];
    if (!Array.isArray(partitions) || partitions.length === 0) return [];

    // If availableCapacity is provided, filter by what fits
    let filteredPartitions = partitions;
    if (availableCapacity !== undefined) {
      filteredPartitions = partitions.filter(capacity => capacity <= availableCapacity);
    }

    // Sort by capacity (descending)
    const sorted = [...filteredPartitions].sort((a, b) => b - a);

    // Map to dropdown options
    return sorted.map(capacity => ({
      key: capacity,
      value: capacity,
      text: `${capacity} ${t('capacity.units')} (${Math.round((capacity / resource.totalCapacityUnits) * 100)}%)`,
      disabled: availableCapacity !== undefined && capacity > availableCapacity
    }));
  }, [resource, availableCapacity, t]);

  if (!resource) {
    return (
      <Message warning>
        <Message.Header>{t('capacity.selectAssetFirst')}</Message.Header>
      </Message>
    );
  }

  // Check if no capacity options available
  if (capacityOptions.length === 0) {
    return (
      <Message error>
        <Message.Header>{t('capacity.noPartitionsAvailable')}</Message.Header>
        <p>{t('assetForm.createPartitionFirst')}</p>
      </Message>
    );
  }

  // Check if not enough capacity
  if (availableCapacity !== undefined && availableCapacity <= 0) {
    return (
      <Message error>
        <Message.Header>{t('capacity.fullyBooked')}</Message.Header>
        <p>{t('capacity.notEnoughAvailable')}</p>
      </Message>
    );
  }

  return (
    <Form.Field required={required}>
      <label>{t('capacity.selectCapacity')}</label>
      <Dropdown
        placeholder={t('capacity.chooseCapacity')}
        fluid
        selection
        options={capacityOptions}
        value={selectedCapacity || ''}
        onChange={(_, { value }) => onCapacityChange(Number(value))}
      />
      {availableCapacity !== undefined && (
        <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
          {t('capacity.available')}: {availableCapacity} {t('capacity.units')}
        </div>
      )}
    </Form.Field>
  );
});

export default CapacitySelector;